// productlaneVisibility — gates the Productlane changelog/feedback bubble so it
// behaves like a FIRST-RUN nudge, not a permanent fixture.
//
// WHY (product intent): Productlane's bottom-right launcher used to show on every
// authenticated dashboard page, forever. The desired behavior is: surface it for
// a brand-new user's first few sessions so they discover it, then get out of the
// way — not the 100th login, not "a month since the last changelog post".
//
// POLICY: show changelog notifications only while the user is within their first
// N (=3) DISTINCT browser sessions. After that the notification never auto-appears
// again, while the bottom-bar "Help Center" button can still open the enabled
// widget on demand.
//
// STORAGE (client-only, no backend): the session count lives in localStorage,
// keyed per user. It is incremented at most ONCE per browser session, guarded by
// a sessionStorage flag, so React re-renders / StrictMode double-invokes / extra
// tabs can't inflate it. "3 sessions" therefore means 3 real logins, not 3
// effect fires. Tradeoff (accepted): the count is per-device and user-clearable,
// so a new browser / cleared storage / incognito resets it and the user may see
// the bubble a few more times. That is benign for a changelog nudge; making it
// truly once-per-human-forever would require a server-side field (out of scope).

/** Number of distinct sessions during which the bubble may auto-appear. */
export const PRODUCTLANE_MAX_SESSIONS = 3;

const COUNT_KEY_PREFIX = 'cs_productlane_session_count_';
// Session-scoped guard so we increment the persistent count only once per browser
// session regardless of how many times the gating effect runs.
const COUNTED_THIS_SESSION_KEY = 'cs_productlane_counted_this_session';

/** localStorage key holding the per-user session count. */
function countKey(userId) {
  return `${COUNT_KEY_PREFIX}${userId}`;
}

/**
 * Read the persisted session count for a user. Returns 0 on any storage/parse
 * failure (fail-open toward showing the nudge for a genuinely new user).
 */
export function getProductlaneSessionCount(userId) {
  if (!userId || typeof window === 'undefined') return 0;
  try {
    const raw = window.localStorage.getItem(countKey(userId));
    const n = raw == null ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * Increment the per-user session count, but ONLY the first time this is called
 * in the current browser session (sessionStorage guard). Idempotent within a
 * session. Returns the count AFTER any increment so the caller can gate on a
 * single read without a second storage round-trip.
 *
 * Best-effort: if storage is unavailable, returns 0 (treated as "new user" — the
 * bubble shows, which is the safe/desired default for a discovery nudge).
 */
export function recordProductlaneSessionOnce(userId) {
  if (!userId || typeof window === 'undefined') return 0;
  try {
    if (window.sessionStorage.getItem(COUNTED_THIS_SESSION_KEY) === userId) {
      // Already counted this session for this user — return the current value.
      return getProductlaneSessionCount(userId);
    }
    const next = getProductlaneSessionCount(userId) + 1;
    window.localStorage.setItem(countKey(userId), String(next));
    // Key the guard by userId so an account switch within one tab session still
    // counts the new user's first session.
    window.sessionStorage.setItem(COUNTED_THIS_SESSION_KEY, userId);
    return next;
  } catch {
    return 0;
  }
}

/**
 * Whether the Productlane bubble should auto-appear, given the user's recorded
 * session count. Pure predicate — does NOT touch storage (callers record the
 * session separately so this stays side-effect-free and testable).
 */
export function isWithinProductlaneSessionWindow(sessionCount) {
  return sessionCount > 0 && sessionCount <= PRODUCTLANE_MAX_SESSIONS;
}

/**
 * Keep Productlane's page-wide availability separate from its first-run
 * changelog notification. Productlane.disable() prevents custom triggers from
 * opening the widget, so authenticated dashboard pages must stay enabled even
 * after the notification window ends.
 */
export function configureProductlaneWidget(
  productlane,
  { isAvailable, userEmail, showChangelogNotification },
) {
  if (!productlane) return;

  if (!isAvailable) {
    productlane.set({ user: null });
    productlane.disable();
    return;
  }

  productlane.set({
    user: { email: userEmail ?? 'Unknown' },
    disableChangelogNotification: !showChangelogNotification,
  });
  productlane.enable();
}
